'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface HeroSlide {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  ctaText: string;
  ctaLink: string;
  image: string;
  alt: string;
}

const heroSlides: HeroSlide[] = [
{
  id: 1,
  title: 'Your Global Pharmaceutical Partner',
  subtitle: '6000+ Products | 60+ Countries | One Trusted Source',
  description: 'Connecting Indian pharmaceutical excellence with global regulatory standards. Experience seamless trading and comprehensive regulatory consultancy services.',
  ctaText: 'Explore Product Catalog',
  ctaLink: '/product-trading-portal',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17b84bf3b-1765284590370.png",
  alt: 'Modern pharmaceutical laboratory with scientists in white coats working with advanced equipment and blue lighting'
},
{
  id: 2,
  title: 'Regulatory Excellence, Delivered',
  subtitle: 'EU-GMP | WHO-GMP | ANVISA | ISO 9001:2015',
  description: 'Navigate complex regulatory landscapes with confidence. Our expert consultancy services ensure compliance across all major pharmaceutical markets.',
  ctaText: 'Discover Regulatory Services',
  ctaLink: '/regulatory-consultancy-services',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19e1c1eeb-1766491952532.png",
  alt: 'Professional pharmaceutical quality control specialist examining medication samples in modern laboratory with testing equipment'
},
{
  id: 3,
  title: 'Where Indian Quality Meets Global Standards',
  subtitle: 'Trusted Manufacturing | International Compliance',
  description: 'Bridge the gap between world-class Indian manufacturing and international market requirements. Your strategic partner for pharmaceutical success.',
  ctaText: 'Learn About Our Global Presence',
  ctaLink: '/global-presence',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1031f59ea-1764660301435.png",
  alt: 'International pharmaceutical distribution network with packages and global shipping containers in modern warehouse facility'
}];


export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const handlePrevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const handleNextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const handleDotClick = (index: number) => {
    setIsAutoPlaying(false);
    setCurrentSlide(index);
  };

  return (
    <section className="relative bg-gradient-to-br from-primary/5 via-background to-secondary/5 overflow-hidden">
      <div className="container mx-auto px-4 py-12 lg:py-16 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Content */}
          <div className="space-y-4 lg:space-y-6">
            <div className="inline-flex items-center space-x-2 px-3 py-1.5 bg-primary/10 rounded-full">
              <Icon name="CheckBadgeIcon" size={18} className="text-primary" variant="solid" />
              <span className="text-xs font-medium text-primary">Certified Excellence Since 2020</span>
            </div>

            <h1 className="font-heading font-bold text-3xl lg:text-4xl xl:text-5xl text-textPrimary leading-tight break-words">
              {heroSlides[currentSlide].title}
            </h1>

            <p className="font-heading text-base lg:text-lg text-secondary font-semibold break-words">
              {heroSlides[currentSlide].subtitle}
            </p>

            <p className="text-sm lg:text-base text-textSecondary leading-relaxed break-words">
              {heroSlides[currentSlide].description}
            </p>

            {/* Explore Product Catalogue Button */}
            <div className="pt-2">
              <a
                href="https://docs.google.com/spreadsheets/d/1QJVxOO0iT_LMb9zygy3Zaq7Mdq4p6D27tEMXjNhvNl0/edit?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-5 py-2.5 bg-secondary text-white font-semibold text-sm rounded-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                Explore Product Catalogue
                <Icon name="DocumentTextIcon" size={16} className="ml-2" />
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                href={heroSlides[currentSlide].ctaLink}
                className="inline-flex items-center justify-center px-6 py-3 bg-cta text-cta-foreground font-cta font-semibold text-sm rounded-md hover:-translate-y-1 hover:shadow-cta transition-all duration-300">

                {heroSlides[currentSlide].ctaText}
                <Icon name="ArrowRightIcon" size={18} className="ml-2" />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-3 bg-background text-textPrimary border-2 border-border font-semibold text-sm rounded-md hover:bg-muted transition-all duration-300">

                Schedule Consultation
                <Icon name="CalendarIcon" size={18} className="ml-2" />
              </Link>
            </div>

            {/* Slide Indicators */}
            <div className="flex items-center space-x-3 pt-2">
              {heroSlides.map((_, index) =>
              <button
                key={index}
                onClick={() => handleDotClick(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'w-8 bg-primary' : 'w-2 bg-border hover:bg-primary/50'}`
                }
                aria-label={`Go to slide ${index + 1}`} />

              )}
            </div>
          </div>

          {/* Image Carousel */}
          <div className="relative">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl max-w-full">
              <AppImage
                src={heroSlides[currentSlide].image}
                alt={heroSlides[currentSlide].alt}
                className="w-full h-full object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-textPrimary/20 to-transparent"></div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={handlePrevSlide}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-background/90 hover:bg-background rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              aria-label="Previous slide">

              <Icon name="ChevronLeftIcon" size={20} className="text-textPrimary" />
            </button>
            <button
              onClick={handleNextSlide}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-background/90 hover:bg-background rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              aria-label="Next slide">

              <Icon name="ChevronRightIcon" size={20} className="text-textPrimary" />
            </button>

            {/* Trust Badges */}
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex flex-wrap items-center justify-center gap-2 sm:gap-4 bg-background px-4 py-2 rounded-full shadow-lg max-w-full">
              <div className="flex items-center space-x-1.5">
                <Icon name="ShieldCheckIcon" size={18} className="text-success" variant="solid" />
                <span className="text-xs font-medium text-textPrimary">EU-GMP</span>
              </div>
              <div className="w-px h-4 bg-border"></div>
              <div className="flex items-center space-x-1.5">
                <Icon name="GlobeAltIcon" size={18} className="text-primary" />
                <span className="text-xs font-medium text-textPrimary">WHO-GMP</span>
              </div>
              <div className="w-px h-4 bg-border"></div>
              <div className="flex items-center space-x-1.5">
                <Icon name="CheckBadgeIcon" size={18} className="text-secondary" variant="solid" />
                <span className="text-xs font-medium text-textPrimary">ISO 9001</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}